import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import WhatsAppButton from '../components/WhatsAppButton';
import { AuthContext } from '../context/AuthContext';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import api from '../utils/api';
import { hasPartialPackageSelection } from '../components/BookingCalendar';

const toYMD = (d) => {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/** Base price: full package only = package price; outside only = nights × per-night; mixed = package price + (extra nights × per-night). */
function getStayBasePrice(property, checkIn, checkOut) {
  if (!property || !checkIn || !checkOut) return 0;
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const stayDates = new Set();
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) stayDates.add(toYMD(d));

  const perNight = property.price ?? 0;
  const packages = property.packages || [];

  for (const pkg of packages) {
    if (!pkg.isActive || !pkg.selectedDates?.length) continue;
    const pkgNights = getPackageNightSet(pkg);
    if (pkgNights.size === 0) continue;
    const overlap = [...pkgNights].filter((ymd) => stayDates.has(ymd));
    if (overlap.length === 0) continue;
    if (overlap.length === pkgNights.size) {
      const packagePrice = pkg.price ?? 0;
      if (stayDates.size === pkgNights.size) return packagePrice;
      const extraNights = stayDates.size - pkgNights.size;
      return packagePrice + extraNights * perNight;
    }
  }

  return stayDates.size * perNight;
}

/** Package nights = first date to last date EXCLUSIVE (last date = check-out day). Package "1 to 6" = nights 1,2,3,4,5; book check-in 1, check-out 6. */
function getPackageNightSet(pkg) {
  const dates = pkg?.selectedDates;
  if (!Array.isArray(dates) || dates.length === 0) return new Set();
  const ymds = dates.map((d) => toYMD(new Date(d))).filter(Boolean).sort();
  if (ymds.length === 0) return new Set();
  const set = new Set();
  const start = new Date(ymds[0] + "T12:00:00");
  const end = new Date(ymds[ymds.length - 1] + "T12:00:00");
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
    set.add(toYMD(d));
  }
  return set;
}

/** Find package when stay includes full package (exact or with extra nights). Returns { pkg, total, nights, packageNights, extraNights }. */
const getPackageForStay = (property, checkIn, checkOut, lang = 'en') => {
  if (!property?.packages?.length || !checkIn || !checkOut) return null;
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const stayDates = new Set();
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
    stayDates.add(toYMD(d));
  }
  const nights = stayDates.size;
  if (nights < 1) return null;
  const perNight = property.price ?? 0;
  for (const pkg of property.packages) {
    if (!pkg.isActive || !pkg.selectedDates?.length) continue;
    const packageNights = getPackageNightSet(pkg);
    if (packageNights.size === 0) continue;
    const overlap = [...packageNights].filter((d) => stayDates.has(d));
    if (overlap.length !== packageNights.size) continue;
    const packagePrice = pkg.price ?? 0;
    const extraNights = nights - packageNights.size;
    const total = extraNights <= 0 ? packagePrice : packagePrice + extraNights * perNight;
    return {
      pkg,
      total,
      nights,
      packageNights: packageNights.size,
      extraNights: Math.max(0, extraNights)
    };
  }
  return null;
};

const Booking = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getDashboardPath } = useContext(AuthContext);
  const { formatPrice, language } = useLanguageCurrency();
  const [property, setProperty] = useState(null);
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: 1,
  });
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    bankAccount: '',
    bankName: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [earlyCheckInTime, setEarlyCheckInTime] = useState('14:00'); // Default 2:00 PM (14:00)
  const [earlyCheckInAllowed, setEarlyCheckInAllowed] = useState(false);
  const [earlyCheckInLoading, setEarlyCheckInLoading] = useState(false);
  const [specialRequests, setSpecialRequests] = useState('');

  useEffect(() => {
    // Get property and booking data from location state
    if (location.state?.property && location.state?.bookingData) {
      setProperty(location.state.property);
      setBookingData(location.state.bookingData);
      setLoading(false);
    } else {
      // If no state, redirect back to properties
      navigate('/properties');
    }

    // Check if user is logged in
    if (!user) {
      navigate('/login', { state: { redirectTo: '/booking', redirectView: 'booking' } });
    }
  }, [location.state, navigate, user]);

  // Check if early check-in is available for this property + check-in date (only when not booked that day and no late checkout from previous guest)
  useEffect(() => {
    if (!property?.id && !property?._id) return;
    if (!bookingData.checkIn) {
      setEarlyCheckInAllowed(false);
      return;
    }
    let cancelled = false;
    setEarlyCheckInLoading(true);
    api.get('/api/bookings/early-checkin-available', {
      params: { propertyId: property.id || property._id, checkInDate: bookingData.checkIn }
    })
      .then((res) => {
        if (!cancelled) setEarlyCheckInAllowed(Boolean(res.data?.allowed));
      })
      .catch(() => {
        if (!cancelled) setEarlyCheckInAllowed(false);
      })
      .finally(() => {
        if (!cancelled) setEarlyCheckInLoading(false);
      });
    return () => { cancelled = true; };
  }, [property?.id, property?._id, bookingData.checkIn]);

  const calculateNights = () => {
    if (!bookingData.checkIn || !bookingData.checkOut) return 0;
    const checkIn = new Date(bookingData.checkIn);
    const checkOut = new Date(bookingData.checkOut);
    if (checkOut <= checkIn) return 0;
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const nights = calculateNights();
  const packageForStay = useMemo(
    () => getPackageForStay(property, bookingData.checkIn, bookingData.checkOut, language),
    [property, bookingData.checkIn, bookingData.checkOut, language]
  );
  const basePrice = useMemo(
    () => getStayBasePrice(property, bookingData.checkIn, bookingData.checkOut),
    [property, bookingData.checkIn, bookingData.checkOut]
  );
  const insuranceDeposit = Number(property?.insurancePrice) || 0;
  const platformFee = 0; // no longer charged; kept for compatibility with any cached references
  const guestsCount = Number(bookingData.guests) || 1;
  const includedGuests = property?.maxGuests != null ? property.maxGuests : 1;
  const extraFeesPerPerson = Number(property?.extraFeesPerPerson) || 0;
  const extraGuestsFee = guestsCount > includedGuests && nights > 0
    ? (guestsCount - includedGuests) * extraFeesPerPerson * nights
    : 0;
  const discountAmount = appliedPromo?.discountAmount ?? 0;
  
  // Calculate early check-in hours from selected time (standard is 14:00 = 2 PM)
  const earlyCheckInHours = useMemo(() => {
    if (!earlyCheckInTime) return 0;
    const [hours] = earlyCheckInTime.split(':').map(Number);
    if (hours >= 14) return 0; // 2 PM or later = no early check-in
    return Math.max(0, 14 - hours); // e.g. 12:00 = 14 - 12 = 2 hours early
  }, [earlyCheckInTime]);

  // Early check-in: fee per hour as % of price per night (effective = basePrice/nights)
  const effectivePricePerNight = nights > 0 ? basePrice / nights : (property?.price ?? 0);
  const earlyCheckInFeePct = Number(property?.earlyCheckInFeePercentage) || 0;
  const hourlyRate = effectivePricePerNight / 24;
  const earlyCheckInFee = earlyCheckInHours > 0 && earlyCheckInFeePct > 0
    ? earlyCheckInHours * hourlyRate * (earlyCheckInFeePct / 100)
    : 0;
  const totalPrice = Math.max(0, basePrice + insuranceDeposit + extraGuestsFee - discountAmount + earlyCheckInFee);

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    setPaymentDetails({
      cardNumber: '',
      cardName: '',
      expiryDate: '',
      cvv: '',
      bankAccount: '',
      bankName: '',
    });
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/\D/g, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setPaymentDetails(prev => ({
      ...prev,
      cardNumber: formatted
    }));
  };

  const handleExpiryDateChange = (e) => {
    const formatted = formatExpiryDate(e.target.value);
    setPaymentDetails(prev => ({
      ...prev,
      expiryDate: formatted
    }));
  };

  const handleApplyPromo = async () => {
    const code = (promoCodeInput || '').trim();
    if (!code) {
      setPromoError(language === 'ar' ? 'أدخل كود الترويج' : 'Enter a promo code');
      return;
    }
    setPromoError('');
    setPromoLoading(true);
    try {
      const propertyId = property?.id || property?._id;
      const res = await api.get('/api/bookings/promo-code/validate', {
        params: { code, propertyId, amount: basePrice }
      });
      const data = res.data;
      if (data.valid && data.promoCodeId) {
        setAppliedPromo({
          id: data.promoCodeId,
          discountAmount: data.discountAmount ?? 0,
          code: data.code
        });
      } else {
        setAppliedPromo(null);
        setPromoError(data.message || (language === 'ar' ? 'كود غير صالح' : 'Invalid promo code'));
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || (language === 'ar' ? 'فشل التحقق من الكود' : 'Failed to validate promo code');
      setPromoError(msg);
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCodeInput('');
    setPromoError('');
  };

  const validatePayment = () => {
    if (paymentMethod === 'card') {
      if (!paymentDetails.cardNumber || paymentDetails.cardNumber.replace(/\s/g, '').length < 16) {
        setError(t('booking.errorCardNumber'));
        return false;
      }
      if (!paymentDetails.cardName) {
        setError(t('booking.errorCardName'));
        return false;
      }
      if (!paymentDetails.expiryDate || paymentDetails.expiryDate.length < 5) {
        setError(t('booking.errorExpiry'));
        return false;
      }
      if (!paymentDetails.cvv || paymentDetails.cvv.length < 3) {
        setError(t('booking.errorCvv'));
        return false;
      }
    } else if (paymentMethod === 'bank') {
      if (!paymentDetails.bankAccount) {
        setError(t('booking.errorBankAccount'));
        return false;
      }
      if (!paymentDetails.bankName) {
        setError(t('booking.errorBankName'));
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Block partial package: user must book full package or no package overlap
    if (property?.packages?.length && hasPartialPackageSelection(property.packages, bookingData.checkIn, bookingData.checkOut)) {
      setError(
        language === 'ar'
          ? 'لا يمكن حجز جزء من باقة. يرجى اختيار إما كل أيام الباقة (ليالي الباقة كاملة) أو أياماً خارج الباقات فقط. حجز جزء من باقة غير مسموح.'
          : 'You cannot book a partial package. Please select either the full package dates (all nights of the package) or only dates outside any package. Partial package bookings are not allowed.'
      );
      return;
    }

    const numberOfGuests = Math.max(1, Number(bookingData.guests) || 1);
    const checkInDate = bookingData.checkIn && typeof bookingData.checkIn === 'string' && bookingData.checkIn.match(/^\d{4}-\d{2}-\d{2}$/)
      ? bookingData.checkIn
      : bookingData.checkIn
        ? new Date(bookingData.checkIn).toISOString().slice(0, 10)
        : '';
    const checkOutDate = bookingData.checkOut && typeof bookingData.checkOut === 'string' && bookingData.checkOut.match(/^\d{4}-\d{2}-\d{2}$/)
      ? bookingData.checkOut
      : bookingData.checkOut
        ? new Date(bookingData.checkOut).toISOString().slice(0, 10)
        : '';

    if (!checkInDate || !checkOutDate) {
      setError(language === 'ar' ? 'يرجى اختيار تواريخ الدخول والخروج' : 'Please select check-in and check-out dates');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        propertyId: property.id || property._id,
        checkInDate,
        checkOutDate,
        numberOfGuests,
        bookingSource: 'Website',
        ...(specialRequests.trim() && { specialRequests: specialRequests.trim() }),
        ...(earlyCheckInHours > 0 && { earlyCheckInHours, requestedCheckInTime: earlyCheckInTime }),
        ...(packageForStay?.pkg?._id && { packageId: packageForStay.pkg._id }),
        ...(appliedPromo?.id && { promoCodeId: appliedPromo.id }),
      };

      const response = await api.post('/api/bookings', payload);

      const newReservation = response.data?.reservation;
      const bookingId = newReservation?._id ?? response.data?.bookingId;
      if (bookingId && user) {
        navigate(`/client/booking/${bookingId}`, { state: { bookingSuccess: true } });
      } else {
        navigate(user ? '/client' : '/', {
          state: { bookingSuccess: true, view: 'bookings' },
        });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || t('booking.errorBookingFailed');
      setError(msg);
      setSubmitting(false);
    }
  };

  if (loading || !property) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#D5B577' }}></div>
            <p className="text-gray-300">{t('booking.loadingDetails')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
      <Navbar />

      <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8" style={{ width: '100%', minHeight: 'auto' }}>
        <div className="w-full max-w-5xl mx-auto">
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/15 border border-red-400/50 text-red-200 text-sm">
              <p className="font-semibold mb-1">{language === 'ar' ? 'تنبيه' : 'Cannot complete booking'}</p>
              <p>{error}</p>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-300 hover:text-[#D5B577] transition-colors mb-4"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('booking.back')}
            </button>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{t('booking.title')}</h1>
            <p className="text-gray-400 mb-4">{t('booking.subtitle')}</p>
            {property?.id && (
              <a
                href={`/properties/${property.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/properties/${property.id}`, { state: { bookingData } });
                }}
                className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
                style={{ color: '#D5B577' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                {t('booking.changeDates') || 'Change dates'}
              </a>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Property Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Property Summary */}
              <div className="bg-slate-800 rounded-2xl p-6 border" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
                <h2 className="text-2xl font-bold text-white mb-4">{t('booking.propertyDetails')}</h2>
                <div className="flex gap-4 mb-4">
                  <img
                    src={property.image || property.images?.[0]}
                    alt={property.title}
                    className="w-32 h-32 rounded-xl object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-2">{property.title}</h3>
                    <p className="text-gray-400 text-sm mb-2">{property.address || property.location}</p>
                    <div className="flex gap-4 text-sm text-gray-300">
                      <span>{property.beds || property.bedrooms} {t('booking.beds')}</span>
                      <span>{property.baths || property.bathrooms} {t('booking.baths')}</span>
                      {property.sqft && <span>{Math.round(property.sqft * 0.092903)} m²</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking Summary */}
              <div className="bg-slate-800 rounded-2xl p-6 border" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
                <h2 className="text-2xl font-bold text-white mb-4">{t('booking.bookingSummary')}</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-300">{t('booking.checkIn')}</span>
                    <span className="text-white font-semibold">
                      {new Date(bookingData.checkIn).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', { 
                        weekday: 'short', 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">{t('booking.checkOut')}</span>
                    <span className="text-white font-semibold">
                      {new Date(bookingData.checkOut).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', { 
                        weekday: 'short', 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">{t('booking.guests')}</span>
                    <span className="text-white font-semibold">{bookingData.guests}</span>
                  </div>
                  {property?.maxGuests != null && (
                    <div className="flex justify-between text-gray-400 text-sm">
                      <span>{language === 'ar' ? 'السعر الأساسي يشمل حتى' : 'Base rate includes up to'}</span>
                      <span>{property.maxGuests} {language === 'ar' ? 'نزيلاً' : 'guests'}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-300">{t('booking.nights')}</span>
                    <span className="text-white font-semibold">{nights}</span>
                  </div>

                  {/* Early check-in option */}
                  {(property?.earlyCheckInFeePercentage > 0 && earlyCheckInAllowed) && (
                    <div className="mt-4 pt-4 border-t space-y-4" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-5 h-5 flex-shrink-0" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-white">
                          {language === 'ar' ? 'تسجيل دخول مبكر' : 'Early check-in'}
                        </span>
                      </div>
                      <p className="text-gray-400 text-xs -mt-1 mb-3">
                        {language === 'ar' 
                          ? 'الدخول المعياري الساعة 2:00 م. اختر وقتاً أبكر (مثلاً 12:00 م) للدخول المبكر. الرسوم محسوبة لكل ساعة كنسبة من سعر الليلة.'
                          : 'Standard check-in is 2:00 PM. Choose an earlier time (e.g. 12:00 PM) for early check-in. Fee is per hour as a percentage of the nightly rate.'}
                      </p>
                      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">
                            {language === 'ar' ? 'وقت الدخول المطلوب' : 'Requested check-in time'}
                          </label>
                          <input
                            type="time"
                            value={earlyCheckInTime}
                            onChange={(e) => setEarlyCheckInTime(e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-700 border text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#D5B577]/50 focus:ring-offset-0 focus:ring-offset-slate-800 transition-all"
                            style={{ borderColor: 'rgba(213, 181, 119, 0.4)' }}
                          />
                        </div>
                      </div>
                      {/* Display check-in time and fee */}
                      {(() => {
                        const [hours] = earlyCheckInTime.split(':').map(Number);
                        const timeStr = new Date(2000, 0, 1, hours, 0, 0).toLocaleTimeString(
                          language === 'ar' ? 'ar-EG' : 'en-US',
                          { hour: 'numeric', minute: '2-digit', hour12: true }
                        );
                        const isEarly = hours < 14;
                        return (
                          <p className="text-sm font-medium mt-2 flex items-center gap-2" style={{ color: isEarly ? '#D5B577' : '#9CA3AF' }}>
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {language === 'ar'
                              ? `تسجيل الدخول من الساعة ${timeStr}${isEarly ? ` (${earlyCheckInHours} ساعة مبكراً)` : ' (معياري)'}`
                              : `Check-in from ${timeStr}${isEarly ? ` (${earlyCheckInHours} hr early)` : ' (standard)'}`}
                          </p>
                        );
                      })()}
                    </div>
                  )}
                  {earlyCheckInLoading && (property?.earlyCheckInFeePercentage > 0) && (
                    <div className="flex items-center gap-2 py-2 text-gray-400 text-sm mt-2">
                      <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      {language === 'ar' ? 'التحقق من إمكانية الدخول المبكر...' : 'Checking early check-in...'}
                    </div>
                  )}

                  {packageForStay && (
                    <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                      <span className="text-sm font-medium" style={{ color: '#D5B577' }}>
                        {language === 'ar' ? 'باقة مطبقة' : 'Package applied'}: {typeof packageForStay.pkg.name === 'string' ? packageForStay.pkg.name : (packageForStay.pkg.name?.[language?.slice(0, 2)] || packageForStay.pkg.name?.en || packageForStay.pkg.name?.ar || '')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Special requests */}
              <div className="bg-slate-800 rounded-2xl p-6 border" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
                <h2 className="text-xl font-bold text-white mb-3">
                  {language === 'ar' ? 'طلبات خاصة (اختياري)' : 'Special requests (optional)'}
                </h2>
                <textarea
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder={language === 'ar' ? 'أي طلبات خاصة، أو وقت الوصول المتوقع، إلخ.' : 'Any special requests, expected arrival time, etc.'}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-slate-700 border text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D5B577]/50 focus:ring-offset-0 focus:ring-offset-slate-800 transition-all resize-none"
                  style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}
                />
              </div>

              {/* Payment Method Selection */}
              <div className="bg-slate-800 rounded-2xl p-6 border" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
                <h2 className="text-2xl font-bold text-white mb-4">{t('booking.paymentMethod')}</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => handlePaymentMethodChange('card')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'card'
                        ? 'border-[#D5B577] bg-[#D5B577]/10'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <svg className="w-8 h-8" style={{ color: paymentMethod === 'card' ? '#D5B577' : '#9CA3AF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <div className="text-left">
                        <div className="font-semibold text-white">{t('booking.cardPayment')}</div>
                        <div className="text-sm text-gray-400">{t('booking.cardPaymentDesc')}</div>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handlePaymentMethodChange('bank')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'bank'
                        ? 'border-[#D5B577] bg-[#D5B577]/10'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <svg className="w-8 h-8" style={{ color: paymentMethod === 'bank' ? '#D5B577' : '#9CA3AF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <div className="text-left">
                        <div className="font-semibold text-white">{t('booking.bankTransfer')}</div>
                        <div className="text-sm text-gray-400">{t('booking.bankTransferDesc')}</div>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Payment Form */}
                <form onSubmit={handleSubmit}>
                  {paymentMethod === 'card' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2" style={{ color: '#D5B577' }}>
                          {t('booking.cardNumber')}
                        </label>
                        <input
                          type="text"
                          name="cardNumber"
                          value={paymentDetails.cardNumber}
                          onChange={handleCardNumberChange}
                          placeholder={t('booking.cardNumberPlaceholder')}
                          maxLength="19"
                          className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-all"
                          style={{
                            backgroundColor: 'rgba(29, 42, 59, 0.7)',
                            border: '1px solid rgba(213, 181, 119, 0.3)',
                            color: 'white'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                          onBlur={(e) => e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)'}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2" style={{ color: '#D5B577' }}>
                          {t('booking.cardName')}
                        </label>
                        <input
                          type="text"
                          name="cardName"
                          value={paymentDetails.cardName}
                          onChange={handleInputChange}
                          placeholder={t('booking.cardNamePlaceholder')}
                          className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-all"
                          style={{
                            backgroundColor: 'rgba(29, 42, 59, 0.7)',
                            border: '1px solid rgba(213, 181, 119, 0.3)',
                            color: 'white'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                          onBlur={(e) => e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)'}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2" style={{ color: '#D5B577' }}>
                            {t('booking.expiryDate')}
                          </label>
                          <input
                            type="text"
                            name="expiryDate"
                            value={paymentDetails.expiryDate}
                            onChange={handleExpiryDateChange}
                            placeholder={t('booking.expiryPlaceholder')}
                            maxLength="5"
                            className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-all"
                            style={{
                              backgroundColor: 'rgba(29, 42, 59, 0.7)',
                              border: '1px solid rgba(213, 181, 119, 0.3)',
                              color: 'white'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                            onBlur={(e) => e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)'}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2" style={{ color: '#D5B577' }}>
                            {t('booking.cvv')}
                          </label>
                          <input
                            type="text"
                            name="cvv"
                            value={paymentDetails.cvv}
                            onChange={handleInputChange}
                            placeholder={t('booking.cvvPlaceholder')}
                            maxLength="4"
                            className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-all"
                            style={{
                              backgroundColor: 'rgba(29, 42, 59, 0.7)',
                              border: '1px solid rgba(213, 181, 119, 0.3)',
                              color: 'white'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                            onBlur={(e) => e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)'}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'bank' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2" style={{ color: '#D5B577' }}>
                          {t('booking.bankName')}
                        </label>
                        <input
                          type="text"
                          name="bankName"
                          value={paymentDetails.bankName}
                          onChange={handleInputChange}
                          placeholder={t('booking.bankNamePlaceholder')}
                          className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-all"
                          style={{
                            backgroundColor: 'rgba(29, 42, 59, 0.7)',
                            border: '1px solid rgba(213, 181, 119, 0.3)',
                            color: 'white'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                          onBlur={(e) => e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)'}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2" style={{ color: '#D5B577' }}>
                          {t('booking.bankAccount')}
                        </label>
                        <input
                          type="text"
                          name="bankAccount"
                          value={paymentDetails.bankAccount}
                          onChange={handleInputChange}
                          placeholder={t('booking.bankAccountPlaceholder')}
                          className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-all"
                          style={{
                            backgroundColor: 'rgba(29, 42, 59, 0.7)',
                            border: '1px solid rgba(213, 181, 119, 0.3)',
                            color: 'white'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                          onBlur={(e) => e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)'}
                        />
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="mt-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50 text-red-300 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full mt-6 py-4 text-lg font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                    onMouseEnter={(e) => !submitting && (e.target.style.backgroundColor = '#c4a366')}
                    onMouseLeave={(e) => !submitting && (e.target.style.backgroundColor = '#D5B577')}
                  >
                    {submitting ? t('booking.processing') : `${t('booking.confirmPay')} ${formatPrice(totalPrice)}`}
                  </button>
                </form>
              </div>
            </div>

            {/* Right Column - Price Summary */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                {/* Promo code */}
                <div className="bg-slate-800 rounded-2xl p-6 border mb-6 min-w-0 overflow-hidden" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
                  <h2 className="text-lg font-bold text-white mb-3">{language === 'ar' ? 'كود الترويج' : 'Promo code'}</h2>
                  {appliedPromo ? (
                    <div className="flex items-center justify-between gap-2 p-3 rounded-xl bg-green-500/10 border border-green-400/30">
                      <span className="text-green-300 font-medium">{appliedPromo.code}</span>
                      <button
                        type="button"
                        onClick={handleRemovePromo}
                        className="text-sm font-medium px-2 py-1 rounded hover:bg-green-500/20 transition-colors"
                        style={{ color: '#D5B577' }}
                      >
                        {language === 'ar' ? 'إزالة' : 'Remove'}
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2 min-w-0">
                      <input
                        type="text"
                        value={promoCodeInput}
                        onChange={(e) => { setPromoCodeInput(e.target.value.toUpperCase()); setPromoError(''); }}
                        placeholder={language === 'ar' ? 'أدخل الكود' : 'Enter code'}
                        className="min-w-0 flex-1 px-4 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-gray-500 focus:outline-none focus:border-[#D5B577]"
                      />
                      <button
                        type="button"
                        onClick={handleApplyPromo}
                        disabled={promoLoading}
                        className="flex-shrink-0 px-4 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
                        style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                      >
                        {promoLoading ? (language === 'ar' ? 'جاري...' : 'Applying...') : (language === 'ar' ? 'تطبيق' : 'Apply')}
                      </button>
                    </div>
                  )}
                  {promoError && (
                    <p className="text-red-400 text-sm mt-2">{promoError}</p>
                  )}
                </div>
                <div className="bg-slate-800 rounded-2xl p-6 border" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
                  <h2 className="text-xl font-bold text-white mb-4">{t('booking.priceSummary')}</h2>
                  <div className="space-y-3 mb-4 pb-4 border-b" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                    {packageForStay ? (
                      <>
                        <div className="flex justify-between text-gray-300">
                          <span>
                            {typeof packageForStay.pkg.name === 'string' ? packageForStay.pkg.name : (packageForStay.pkg.name?.[language?.slice(0, 2)] || packageForStay.pkg.name?.en || '')}
                            {packageForStay.packageNights ? ` (${packageForStay.packageNights} ${t('booking.nightsText')})` : ''}
                          </span>
                          <span>{formatPrice(packageForStay.pkg.price ?? 0)}</span>
                        </div>
                        {packageForStay.extraNights > 0 && (
                          <div className="flex justify-between text-gray-300">
                            <span>{packageForStay.extraNights} {t('booking.nightsText')} {language === 'ar' ? 'خارج الباقة' : 'outside package'}</span>
                            <span>{formatPrice(packageForStay.extraNights * (property?.price ?? 0))}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex justify-between text-gray-300">
                        <span>{nights} {t('booking.nightsText')}</span>
                        <span>{formatPrice(basePrice)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-gray-300">
                      <span>{t('booking.insuranceDeposit')}</span>
                      <span>{formatPrice(insuranceDeposit)}</span>
                    </div>
                    {extraGuestsFee > 0 && (
                      <div className="flex justify-between text-gray-300">
                        <span>{language === 'ar' ? `ضيوف إضافيون (${guestsCount - includedGuests})` : `Extra guests (${guestsCount - includedGuests})`}</span>
                        <span>{formatPrice(extraGuestsFee)}</span>
                      </div>
                    )}
                    {earlyCheckInFee > 0 && (
                      <div className="flex justify-between items-center text-gray-300">
                        <span className="flex items-center gap-1.5">
                          <span className="text-gray-500">↶</span>
                          {language === 'ar' ? `دخول مبكر (${earlyCheckInHours} ${language === 'ar' ? 'س' : 'hr'})` : `Early check-in (${earlyCheckInHours} hr)`}
                        </span>
                        <span className="font-medium text-white">{formatPrice(earlyCheckInFee)}</span>
                      </div>
                    )}
                    {appliedPromo && (
                      <div className="flex justify-between text-green-400">
                        <span>{language === 'ar' ? 'خصم كود الترويج' : 'Promo discount'}</span>
                        <span>-{formatPrice(appliedPromo.discountAmount)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-center pt-4">
                    <span className="text-xl font-bold text-white">{t('booking.total')}</span>
                    <span className="text-2xl font-bold" style={{ color: '#D5B577' }}>{formatPrice(totalPrice)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WhatsAppButton phoneNumber="+1234567890" message={t('common.whatsAppMessageBooking')} />
    </div>
  );
};

export default Booking;
